import { Autocomplete, Box, Grid, TextField, Typography } from '@mui/material'
import { FC, useState, useReducer, useEffect } from 'react'
import { Customer } from '../../../services'
import { fetchCustomerById } from '../../../services/customers'
import { AppDialog } from '../../../components'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import {
  addCustomerAction,
  addCustomerResetAction,
  fetchCustomersActionV2 as fetchCustomersAction,
} from '../../../store/customers'
import { showAlertAction } from '../../../store/alerts'
import {
  isValidMobileNumber,
  indianMobileNumberRegex,
  isValidGST,
  setDefaultGSTType,
  getDefaultGstType,
} from '../../../utils'
import SelectStateItem, { StateOption, getState } from './SelectStateItem'
import { states } from '../../../utils/appUtils'
import { fetchGSTINAction } from '../../../store/gstr'
import GSTINDetailsItemV2 from '../../../../src/components/GSTINDetailsItemV2'

type AddCustomerFields = 'name' | 'mobile' | 'address' | 'address2' | 'gstin' | 'pan' | 'place' | 'pincode'
type AddCustomerError = Record<AddCustomerFields, Record<string, string>>
const addCustomerErrors: AddCustomerError = {
  name: {
    empty: '',
  },
  mobile: {
    empty: '',
  },
  address: {
    empty: '',
  },
  address2: {
    empty: '',
  },
  gstin: {
    empty: '',
  },
  pan: {
    empty: '',
  },
  place: {
    empty: '',
  },
  pincode: {
    empty: 'Pincode must be exactly 6 digits',
  },
}

type InputBox = {
  value: string
  error: boolean
  helperText: string
}

type AddCustomerFieldsState = Record<AddCustomerFields, InputBox>

const defaultObj: InputBox = {
  value: '',
  error: false,
  helperText: '',
}

const initialAddCustomerState: AddCustomerFieldsState = {
  name: { ...defaultObj },
  mobile: { ...defaultObj },
  address: { ...defaultObj },
  address2: { ...defaultObj },
  gstin: { ...defaultObj },
  pan: { ...defaultObj },
  place: { ...defaultObj },
  pincode: { ...defaultObj },
}

type Action = {
  type: 'SET' | 'ERROR'
  payload: {
    name: AddCustomerFields
    value: string
  }
}
const addCustomerReducer = (state, action) => {
  const {
    type,
    payload: { name, value },
  } = action
  switch (type) {
    case 'SET':
      return {
        ...state,
        [name]: { ...state[name], error: false, value: value, helperText: '' },
      }
    case 'ERROR':
      const nameObj = addCustomerErrors[name]
      return {
        ...state,
        [name]: {
          ...state[name],
          error: true,
          value: value,
          helperText: nameObj ? nameObj['empty'] : '',
        },
      }
    default:
      return state
  }
}

export interface AddCustomerProps {
  children?: React.ReactNode
  customer?: Customer
  onClose?: (customer?: Customer) => void
}
interface GSTRegType {
  label: string
  index: number
  value: string
}

const AddCustomer: FC<AddCustomerProps> = ({ onClose, children, customer }) => {
  const [stateOption, setStateOption] = useState<StateOption | null>()
  const gstTypes = [
    { label: 'Registered', index: 0, value: 'REGULAR' },
    { label: 'Unregistered (URP)', index: 1, value: 'URG_CONSUMER' },
  ]
  const [gstRegType, setGstRegType] = useState<GSTRegType>(gstTypes[0])
  const {
    data,
    success: addCustomerSuccess,
    error: addCustomerError,
    message,
  } = useAppSelector((state) => state.addCustomer)

  const dispatchAction = useAppDispatch()
  const [addCustomerData, dispatch] = useReducer(
    addCustomerReducer,
    initialAddCustomerState
  )

  const { name, mobile, address, address2, gstin, pan, place, pincode } = addCustomerData
  const [isClose, setClose] = useState<boolean>(false)
  const [stateError, setStateError] = useState<boolean>(false)
  const [gstinError, setGstinError] = useState<boolean>(false)
  const [fullCustomer, setFullCustomer] = useState<Customer | null>(null)

  useEffect(() => {
    const index = Number(getDefaultGstType())
    const savedType = gstTypes[index]
    if (savedType) {
      setGstRegType(savedType)
    }
  }, [])
  

  const onChangeHandler = (e) => {
    const {
      target: { name, value },
    } = e
    if (value.length > 0 || name === 'mobile' || name === 'address2' || name === 'place' || name === 'pincode') {
      dispatch({ type: 'SET', payload: { name, value } })
    } else {
      dispatch({ type: 'ERROR', payload: { name, value } })
    }
    if (name === 'gstin' && value.length === 15) {
      dispatchAction(fetchGSTINAction(value))
    }
  }

  function getRegType(regType?: string) {
    let regtType = gstTypes[0]
    gstTypes.map((type) => {
      if (type.value === regType) {
        regtType = type
      }
    })
    return regtType
  }

  const submitHandle = () => {
    let error = false
    if (!name?.value) {
      error = true
      dispatch({ type: 'ERROR', payload: { name: 'name' } })
    }
    const mobileTrimmed = (mobile?.value ?? '').trim()
    if (mobileTrimmed && !isValidMobileNumber(mobileTrimmed)) {
      error = true
      dispatch({
        type: 'ERROR',
        payload: { name: 'mobile', value: mobile.value },
      })
    }
    if (!gstRegType) {
      error = true
    }
    const gstinVisible = !(
      gstRegType?.value === 'URG_BUSINESS' ||
      gstRegType?.value === 'URG_CONSUMER' ||
      gstRegType?.value === 'OVERSEAS'
    )
    if (gstinVisible && (!gstin?.value || !isValidGST(gstin?.value))) {
      error = true
      setGstinError(true)
      dispatch({
        type: 'ERROR',
        payload: { name: 'gstin' },
      })
    }

    if (!address?.value) {
      error = true
      dispatch({ type: 'ERROR', payload: { name: 'address' } })
    }
    
    const pincodeValue = (pincode?.value ?? '').trim()
    if (pincodeValue && (pincodeValue.length !== 6 || isNaN(Number(pincodeValue)))) {
      error = true
      dispatch({
        type: 'ERROR',
        payload: { name: 'pincode', value: pincode.value },
      })
    }

    if (gstinVisible && !stateOption) {
      error = true
      setStateError(true)
      dispatch({ type: 'ERROR', payload: { name: 'state' } })
    }

    if (!error) {
      dispatchAction(
        addCustomerAction({
          id: customer?.id,
          name: name.value,
          mobile: mobileTrimmed,
          address: address.value,
          address2: address2.value || undefined,
          gstin: gstinVisible ? gstin.value : '',
          stateCode: stateOption?.code,
          state: stateOption?.label,
          pan: pan.value,
          registrationType: gstRegType.value,
          place: place.value || undefined,
          pincode: pincode.value || undefined,
        })
      )
    }
  }

  useEffect(() => {
    if (customer?.id) {
      fetchCustomerById(customer.id)
        .then((response) => {
          const result = response?.data
          if (result?.success && result?.data) {
            setFullCustomer(result.data)
          }
        })
        .catch(() => {})
    }
  }, [customer?.id])

  useEffect(() => {
    const src = fullCustomer ?? customer
    if (src) {
      if (!src.registrationType && !src.gstin) {
        setGstRegType(gstTypes[1])
      } else {
        setGstRegType(getRegType(src.registrationType))
      }
      dispatch({
        type: 'SET',
        payload: { name: 'mobile', value: src.mobile },
      })
      dispatch({ type: 'SET', payload: { name: 'name', value: src.name } })
      dispatch({
        type: 'SET',
        payload: { name: 'address', value: src.address },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'address2', value: src.address2 ?? '' },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'gstin', value: src.gstin },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'pan', value: src.pan },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'place', value: src.place ?? '' },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'pincode', value: src.pincode ?? '' },
      })

      const rawCode = src.stateCode != null ? String(src.stateCode) : ''
      const normalizedCode = rawCode ? rawCode.padStart(2, '0') : ''
      const stateLabel = src.state ? String(src.state) : ''

      const byCode = normalizedCode ? getState(normalizedCode) : null
      const byLabel =
        !byCode && stateLabel
          ? states.find(
              (s) => s.label.toLowerCase() === stateLabel.toLowerCase()
            ) ?? null
          : null

      const resolved =
        byCode ??
        byLabel ??
        (normalizedCode || stateLabel
          ? { code: normalizedCode, label: stateLabel }
          : null)

      if (resolved) {
        setStateOption(resolved)
      }
    }
  }, [fullCustomer, customer, dispatch])

  useEffect(() => {
    // Refresh the customer list
    //dispatchAction(fetchCustomersResetAction())
    if (addCustomerSuccess) {
      dispatchAction(fetchCustomersAction())
      dispatchAction(addCustomerResetAction())
      dispatchAction(
        showAlertAction({ message: 'Done successfully', severity: 'success' })
      )
      onClose(data)
    } else if (message) {
      dispatchAction(showAlertAction({ message: message, severity: 'error' }))
      dispatchAction(addCustomerResetAction())
    }
  }, [addCustomerSuccess, message])

  const onSelectChange = (e: any, newValue: GSTRegType | null) => {
    if (newValue) {
      setDefaultGSTType(newValue.index+"")
      setGstRegType(newValue)
    }
  }

  return (
    <AppDialog
      id="add-customer"
      title={!customer ? 'Add Party' : 'Edit Party'}
      submitButtonText={'SUBMIT'}
      onClose={onClose}
      close={isClose}
      onSubmit={submitHandle}
      maxWidth="sm"
    >
      <Box
        component="form"
        sx={{
          '& .MuiTextField-root': { m: 1, width: '25ch' },
        }}
        noValidate
        autoComplete="off"
      >
        <Grid container>
          <Grid item={true} xs={11.6}>
            <Autocomplete
              size="small"
              disablePortal
              id="gstin_type_options"
              value={gstRegType}
              options={gstTypes}
              onChange={onSelectChange}
              renderInput={(params) => (
                <TextField
                  size="small"
                  {...params}
                  label="Select GST Registration Type"
                  required
                  error={!gstRegType}
                  sx={{ minWidth: '100%', margin: 0 }}
                />
              )}
              renderOption={(props, option) => (
                <Grid component={'li'} container {...props}>
                  <Grid
                    id={option.label}
                    data-cy={option.label}
                    item
                    xs
                    pl={1}
                    pr={1}
                    pt={0.7}
                    pb={0.7}
                  >
                    {option.label}
                  </Grid>
                </Grid>
              )}
            />
          </Grid>
          {!(
            gstRegType?.value === 'URG_BUSINESS' ||
            gstRegType?.value === 'URG_CONSUMER' ||
            gstRegType?.value === 'OVERSEAS'
          ) && (
            <>
            <Grid item xs={12}>
             
              <GSTINDetailsItemV2
                minWidth={545}
                gstinError={gstinError}
                initialGstin={gstin.value}
                required
                onUpdate={(gstin, gstinObj) => {
                  setGstinError(false)
                  dispatch({
                    type: 'SET',
                    payload: { name: 'gstin', value: gstin },
                  })
                  if (gstin && gstin.length > 2 && !stateOption) {
                    setStateOption(getState(gstin.substring(0, 2)))
                  }
                  if (
                    gstin &&
                    gstin.length > 11 &&
                    (!pan.value || pan.value.length == 0)
                  ) {
                    dispatch({
                      type: 'SET',
                      payload: { name: 'pan', value: gstin.substring(2, 12) },
                    })
                  }
                  if (gstinObj && gstinObj.tradeName) {
                    dispatch({
                      type: 'SET',
                      payload: { name: 'name', value: gstinObj.tradeName },
                    })
                    dispatch({
                      type: 'SET',
                      payload: {
                        name: 'address',
                        value: gstinObj.address1 || '',
                      },
                    })
                    dispatch({
                      type: 'SET',
                      payload: {
                        name: 'address2',
                        value: gstinObj.address2 || '',
                      },
                    })
                    if (gstinObj.pinCode) {
                      dispatch({
                        type: 'SET',
                        payload: { name: 'pincode', value: gstinObj.pinCode },
                      })
                    }
                    if (gstinObj.place) {
                      dispatch({
                        type: 'SET',
                        payload: { name: 'place', value: gstinObj.place },
                      })
                    }
                    setStateOption(getState(gstinObj.stateCode))
                  }
                }}
              />
            </Grid>
              <Grid item xs={12}>
              <TextField
                size="small"
                name="pan"
                value={pan.value}
                label="PAN Number"
                error={pan.value && pan.error}
                helperText={pan.helperText}
                onChange={onChangeHandler}
                sx={{ minWidth: 545 }}
                InputLabelProps={{ shrink: pan.value }}
              ></TextField>
            </Grid>
            </>
          )}
          <Grid item xs>
            <TextField
              size="small"
              name="name"
              value={name.value}
              label="Name"
              error={name.error}
              required
              helperText={name.helperText}
              onChange={onChangeHandler}
              sx={{ minWidth: 545 }}
              InputLabelProps={{ shrink: !!name.value }}
            ></TextField>
          </Grid>
          <Grid item xs={12} mt={1}>
            <TextField
              size="small"
              name="address"
              value={address.value}
              label="Billing Address"
              error={address.error}
              required
              helperText={address.helperText}
              onChange={onChangeHandler}
              sx={{ minWidth: 545 }}
              InputLabelProps={{ shrink: !!address.value }}
            ></TextField>
          </Grid>
          <Grid item xs={12} mt={1}>
            <TextField
              size="small"
              name="address2"
              value={address2.value}
              label="Billing Address Line 2"
              error={address2.error}
              helperText={address2.helperText}
              onChange={onChangeHandler}
              sx={{ minWidth: 545 }}
              InputLabelProps={{ shrink: !!address2.value }}
            ></TextField>
          </Grid>
          <Grid item xs={12} mt={1}>
            <Box sx={{ display: 'flex', gap: 1, minWidth: 545 }}>
              <TextField
                size="small"
                name="place"
                value={place.value}
                label="Place (Optional)"
                onChange={onChangeHandler}
                sx={{ flex: 1, m: 0 }}
                InputLabelProps={{ shrink: !!place.value }}
              />
              <TextField
                size="small"
                name="pincode"
                value={pincode.value}
                label="Pincode (Optional)"
                error={pincode.error}
                helperText={pincode.error ? pincode.helperText : ''}
                onChange={onChangeHandler}
                inputProps={{ maxLength: 6 }}
                sx={{ width: 160, m: 0 }}
                InputLabelProps={{ shrink: !!pincode.value }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} mt={1}>
            <SelectStateItem
              error={stateError}
              minWidth={545}
              selectedStateOption={stateOption}
              onChange={(option) => {
                setStateError(false)
                setStateOption(option)
              }}
            />
          </Grid>

          <Grid item xs={12} mt={1}>
            <TextField
              size="small"
              name="mobile"
              value={mobile.value}
              label="Mobile(Optional)"
              type="string"
              error={mobile.error}
              helperText={mobile.helperText}
              onChange={onChangeHandler}
              fullWidth
              inputProps={{ maxLength: 13, pattern: indianMobileNumberRegex }}
              sx={{ minWidth: 545 }}
              InputLabelProps={{ shrink: !!mobile.value }}
            ></TextField>
          </Grid>
        </Grid>
      </Box>
    </AppDialog>
  )
}

export default AddCustomer
